import React, { useMemo, useState } from 'react';
import { AESTHETICS, getDesignsByAesthetic, countAvailable } from './data';
import DesignCard from './DesignCard';
import DesignPreviewPanel from './DesignPreviewPanel';

// DesignsPage — voucher design library.
//
// The salon manages WHICH designs are available to assign to vouchers. The
// per-voucher curation itself happens in the voucher editor (phase 2): each
// voucher.designIds[] cherry-picks from the library entries marked
// `available: true` here.
//
// Layout: banner with copy + global count + bulk actions; aesthetic tabs
// (Editorial / Abstract / Custom); responsive grid of design cards. Only
// the active aesthetic's cards render — perf-friendly and a natural fade
// between tabs.

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
    <path d="M7 2v10M2 7h10" />
  </svg>
);

const WarnIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M7 1l6.5 11.5h-13L7 1z" />
    <path d="M7 5.5v3M7 10.5v.01" />
  </svg>
);

const SparkleHero = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
    <path d="M20 4l3 9 9 3-9 3-3 9-3-9-9-3 9-3 3-9z" fill="currentColor" opacity="0.85" />
    <circle cx="32" cy="10" r="2" fill="currentColor" opacity="0.55" />
    <circle cx="8" cy="32" r="1.5" fill="currentColor" opacity="0.55" />
  </svg>
);

const DesignsPage = ({
  designs,
  designStats = {},
  onToggle,
  vouchers = [],
  onOpenVoucher,
  salonName,
  currency,
  // Custom designer is owned by the parent (GiftCardsV2) so the top-nav
  // Add dropdown can open it from any sub-tab. DesignsPage just wires
  // its "+ Create custom design" tile and edit-on-click to these.
  onCreateCustom,
  onEditCustom,
}) => {
  const [activeAesthetic, setActiveAesthetic] = useState('editorial');
  // Track the previewed design by ID, not by snapshot — when the parent
  // toggles availability, the slide-out's content updates without having
  // to re-set state. (Snapshot-by-object went stale on toggle.)
  const [previewingId, setPreviewingId] = useState(null);

  const previewing = previewingId
    ? designs.find(d => d.id === previewingId) || null
    : null;

  // Counts (memoised — designs is stable between renders unless toggled).
  const totalAvailable = useMemo(() => countAvailable(designs), [designs]);
  const totalCount = designs.length;

  const designsByAesthetic = useMemo(() => {
    const map = {};
    for (const a of AESTHETICS) {
      map[a.id] = getDesignsByAesthetic(designs, a.id);
    }
    return map;
  }, [designs]);

  const activeDesigns = designsByAesthetic[activeAesthetic] || [];
  const activeAvailableCount = activeDesigns.filter(d => d.available).length;
  const allActiveAvailable = activeAvailableCount === activeDesigns.length;

  // Bulk show / hide for the active aesthetic. Fires onToggle per item
  // rather than introducing a new bulk API — keeps the parent simple.
  const handleBulk = (next) => {
    activeDesigns.forEach((d) => {
      if (d.available !== next) onToggle(d.id, next);
    });
  };

  const handleSelectDesign = (design) => setPreviewingId(design.id);
  const handleEditDesign   = (design) => { if (onEditCustom) onEditCustom(design); };
  const handleAddCustom    = () => { if (onCreateCustom) onCreateCustom(); };

  const zeroAvailable = totalAvailable === 0;

  return (
    <>
      {/* Banner — library context + global counter + zero-warning state */}
      <div className={`v2-designs-banner ${zeroAvailable ? 'is-warning' : ''}`}>
        <div className="v2-designs-banner-text">
          <strong>Your voucher design library.</strong>{' '}
          Hide any designs you don't want offered. You'll pick which apply to
          each voucher when you create or edit one.
          {zeroAvailable && (
            <div className="v2-designs-banner-warn">
              <WarnIcon />
              <span>Nothing's available, so vouchers won't have any designs for clients to choose.</span>
            </div>
          )}
        </div>
        <div
          className="v2-designs-banner-counter"
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="v2-designs-banner-num">{totalAvailable}</span>
          <span className="v2-designs-banner-label">of {totalCount} available</span>
        </div>
      </div>

      {/* Aesthetic tabs — chip pattern, with per-aesthetic available count */}
      <div className="v2-aesthetic-tabs" role="tablist">
        {AESTHETICS.map((a) => {
          const tabDesigns = designsByAesthetic[a.id] || [];
          const tabAvailable = tabDesigns.filter(d => d.available).length;
          const isActive = activeAesthetic === a.id;
          return (
            <button
              key={a.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`v2-aesthetic-tab ${isActive ? 'active' : ''}`}
              onClick={() => setActiveAesthetic(a.id)}
            >
              {a.name}
              <span className="v2-aesthetic-tab-count">
                {tabAvailable}/{tabDesigns.length}
              </span>
            </button>
          );
        })}
      </div>

      {/* Bulk action strip — only meaningful when the tab has cards */}
      {activeDesigns.length > 0 && (
        <div className="v2-designs-toolbar">
          <div className="v2-designs-toolbar-label">
            {activeAvailableCount} of {activeDesigns.length} available in this view
          </div>
          <button
            type="button"
            className="v2-designs-toolbar-action"
            onClick={() => handleBulk(!allActiveAvailable)}
          >
            {allActiveAvailable ? 'Hide all' : 'Show all'}
          </button>
        </div>
      )}

      {/* Grid — only renders the active aesthetic for perf */}
      <div className="v2-designs-grid">
        {activeDesigns.map((d) => (
          <DesignCard
            key={d.id}
            design={d}
            salonName={salonName}
            currency={currency}
            onSelect={handleSelectDesign}
            onToggle={onToggle}
            onEdit={handleEditDesign}
          />
        ))}

        {/* Custom tab tail: "+ Create custom design" tile. Sits alongside
            existing customs once the salon has any. */}
        {activeAesthetic === 'custom' && activeDesigns.length > 0 && (
          <button
            type="button"
            className="v2-add-design"
            onClick={handleAddCustom}
            aria-label="Create custom design"
          >
            <PlusIcon />
            <span className="v2-add-design-label">Create custom design</span>
            <span className="v2-add-design-help">Logo, photo, colours, typography</span>
          </button>
        )}
      </div>

      {/* Empty state for Custom (zero customs) — hero CTA instead of an
          orphan dashed tile. */}
      {activeAesthetic === 'custom' && activeDesigns.length === 0 && (
        <div className="v2-custom-empty-hero">
          <div className="v2-custom-empty-illo"><SparkleHero /></div>
          <h3 className="v2-custom-empty-title">Create your first custom design</h3>
          <p className="v2-custom-empty-body">
            Logo, photos, your colours, your typography. The card your clients
            can't get anywhere else.
          </p>
          <button
            type="button"
            className="v2-btn-primary"
            onClick={handleAddCustom}
          >
            <PlusIcon /> New custom design
          </button>
        </div>
      )}

      {/* Slide-out panels — right on desktop, up from bottom on mobile */}
      {previewing && (
        <DesignPreviewPanel
          design={previewing}
          designs={designs}
          stats={designStats}
          vouchers={vouchers}
          salonName={salonName}
          currency={currency}
          onClose={() => setPreviewingId(null)}
          onToggle={onToggle}
          onEdit={(d) => { setPreviewingId(null); if (onEditCustom) onEditCustom(d); }}
          onOpenVoucher={(v) => {
            // Phase 2: route to voucher editor with v pre-loaded. For now,
            // close the panel and surface the intent in the console so we
            // can confirm the cross-reference is wiring up.
            // eslint-disable-next-line no-console
            console.log('[VoucherV2] open voucher from design panel', v?.id);
            if (onOpenVoucher) onOpenVoucher(v);
            setPreviewingId(null);
          }}
          onAssignToVoucher={(d) => {
            // Phase 2: open voucher editor with this design pre-selected.
            // eslint-disable-next-line no-console
            console.log('[VoucherV2] assign design to voucher', d?.id);
          }}
        />
      )}
    </>
  );
};

export default DesignsPage;
